import React, { useEffect, useState } from 'react';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { Download, Mail, Award, Calendar, FileText, Image, ExternalLink, Send, Loader2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PressMedia = () => {
  const [mediaKit, setMediaKit] = useState(null);
  const [loadingKit, setLoadingKit] = useState(true);
  const [showPressForm, setShowPressForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    author_name: '',
    author_email: '',
    author_company: '',
    category: 'General'
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchMediaKit();
    
    // Load Soro blog widget script
    const existingScript = document.getElementById('soro-blog-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'soro-blog-script';
      script.src = 'https://app.trysoro.com/api/embed/9d0256d5-2a61-4ffb-a0f7-bebe94997cf7';
      script.defer = true;
      document.body.appendChild(script);
    }
    
    return () => {
      // Cleanup script on unmount
      const script = document.getElementById('soro-blog-script');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const fetchMediaKit = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/media-kit`);
      const data = await response.json();
      if (data.success) {
        setMediaKit(data);
      }
    } catch (error) {
      // Silent fail - media kit will show loading state
    } finally {
      setLoadingKit(false);
    }
  };

  const handleSubmitPressRelease = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/press-releases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({
          title: '',
          summary: '',
          content: '',
          author_name: '',
          author_email: '',
          author_company: '',
          category: 'General'
        });
      }
    } catch (error) {
      // Silent fail - form will remain in submitting state briefly
    } finally {
      setSubmitting(false);
    }
  };

  const pressReleases = [
    {
      date: '2025',
      title: 'Need A Hair Makeover Wins Multiple UK Hair & Beauty Awards 2025',
      summary: 'Award-winning salon franchise recognized for excellence in hair extensions and hair loss specialist services.',
    },
    {
      date: '2025',
      title: 'Franchise Expansion: London & Dubai Exhibition Announcement',
      summary: 'Need A Hair Makeover announces strategic expansion plans with exhibitions in London and Dubai.',
    },
    {
      date: '2024',
      title: 'Fresha Names Need A Hair Makeover Most Highly Recommended Salon',
      summary: 'Leading booking platform recognizes exceptional customer service and satisfaction ratings.',
    }
  ];

  const awards = [
    { year: '2025', awards: ['UK Hair & Beauty - Hair Extensions & Hairloss Specialist Winner', 'Salon Awards - Best Hair Extensions Salon', 'Women Like Me Awards Winner', 'Top 50 Salon UK'] },
    { year: '2024', awards: ['Fresha - Most Highly Recommended', 'UK Hair & Beauty - Hairloss Specialist Winner'] },
    { year: '2022', awards: ['Best Hair Extensions Salon', 'Mobile & Emerging Technology StartUp Award'] },
    { year: '2020', awards: ['Best Hair Extension Salon', 'Best Creative Image'] },
    { year: '2018', awards: ['Best Designed Salon', 'Best Customer Services', 'Best Hygiene and Cleanliness'] },
    { year: '2017', awards: ['Best Hair Extensions Salon', 'Best Customer Experience'] }
  ];

  const keyFacts = mediaKit?.key_facts ? [
    { label: 'Years of Expertise', value: mediaKit.key_facts.years_experience },
    { label: 'Professionals Trained', value: mediaKit.key_facts.professionals_trained },
    { label: 'Proprietary Techniques', value: mediaKit.key_facts.proprietary_techniques },
    { label: 'Industry Awards', value: mediaKit.key_facts.industry_awards },
    { label: 'Founded', value: 'UK' },
    { label: 'Expansion Markets', value: 'UK, Dubai, Europe' }
  ] : [
    { label: 'Years of Expertise', value: '25+' },
    { label: 'Professionals Trained', value: '3,900+' },
    { label: 'Proprietary Techniques', value: '18+' },
    { label: 'Industry Awards', value: '30+' },
    { label: 'Founded', value: 'UK' },
    { label: 'Expansion Markets', value: 'UK, Dubai, Europe' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEO page="press" />
      <Header onApplyClick={() => window.location.href = '/#apply-form'} />
      
      {/* Hero Section with Image */}
      <div className="pt-20">
        <div className="w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/kpb26hal_Press%20blog%20needahairmakeover%20_20260208_122315_0000.png"
            alt="Press That Blog - There's more to adding hair!"
            className="w-full h-auto max-h-[500px] object-cover"
          />
        </div>
        <div className="bg-white py-8 text-center">
          <div className="container mx-auto px-6 max-w-6xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-3 text-accent-primary">Blog, Press & Media</h1>
            <p className="text-lg text-subtle max-w-2xl mx-auto">
              Latest news, resources for journalists, bloggers, and media professionals
            </p>
          </div>
        </div>
      </div>

      <div className="py-16">
        <div className="container mx-auto px-6 max-w-6xl">
          
          {/* Quick Contact for Press */}
          <div className="bg-accent-primary rounded-2xl p-8 mb-12 -mt-20 relative z-10 shadow-2xl">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              {/* Press Image */}
              <div className="hidden md:block">
                <img 
                  src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=300&fit=crop" 
                  alt="Press and Media Communications"
                  className="rounded-xl w-full h-40 object-cover shadow-lg"
                />
              </div>
              {/* Text Content */}
              <div className="md:col-span-2">
                <h2 className="text-2xl font-bold mb-2" style={{ color: 'white' }}>Media Enquiries</h2>
                <p className="mb-4" style={{ color: 'rgba(255,255,255,0.9)' }}>For press enquiries, interviews, or media partnerships, contact our PR team.</p>
                <a 
                  href="mailto:needahairmakeover@gmail.com"
                  className="inline-flex items-center justify-center bg-white text-accent-primary px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  needahairmakeover@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Key Facts & Figures */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8 text-center">Key Facts & Figures</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {keyFacts.map((fact, index) => (
                <div key={index} className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="text-3xl font-bold text-accent-primary mb-2">{fact.value}</div>
                  <div className="text-sm text-subtle">{fact.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* About Section for Press */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-6">About Need A Hair Makeover</h2>
            <div className="prose prose-lg max-w-none text-subtle">
              <p className="mb-4">
                <strong>Need A Hair Makeover®</strong> is a multi-award-winning specialist salon franchise 
                offering premium hair extensions, hair loss solutions, and bespoke hair services. Founded with 
                over 25 years of industry expertise, the brand has become a leading name in the UK hair and 
                beauty sector.
              </p>
              <p className="mb-4">
                The franchise model focuses on specialist services including 18+ proprietary hair extension 
                techniques, non-invasive hair loss systems, cranial prosthesis consultations, and premium 
                client experiences. The brand serves celebrities, TV personalities, film productions, and 
                premium clients seeking specialist hair solutions.
              </p>
              <p className="mb-4">
                With plans for strategic expansion into Dubai and the GCC region, Need A Hair Makeover 
                represents a significant growth opportunity in the specialist beauty sector, addressing 
                the needs of over 15 million people in the UK alone who experience hair loss.
              </p>
              <p>
                The brand is led by founder <strong>Diane Shawe</strong>, a recognized industry expert who has 
                trained over 3,900 professionals and established the Hair Extensions Training Academy.
              </p>
            </div>
          </div>

          {/* Blog Widget Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8 text-center">Latest from Our Blog</h2>
            <div id="soro-blog" className="min-h-[400px]"></div>
          </div>

          {/* Media Kit & Downloadable Assets */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-dark">Media Kit & Assets</h2>
              <Image className="w-8 h-8 text-accent-primary" />
            </div>
            <p className="text-subtle mb-6">
              Download official logos, images, and brand assets for use in publications and media coverage.
            </p>
            
            {loadingKit ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mediaKit?.assets?.map((asset, index) => (
                  <a 
                    key={index}
                    href={asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-accent-primary/10 rounded-lg flex items-center justify-center mr-3">
                        {asset.type === 'logo' && <Image className="w-5 h-5 text-accent-primary" />}
                        {asset.type === 'photo' && <Image className="w-5 h-5 text-accent-primary" />}
                        {asset.type === 'graphic' && <Image className="w-5 h-5 text-accent-primary" />}
                        {asset.type === 'document' && <FileText className="w-5 h-5 text-accent-primary" />}
                      </div>
                      <div>
                        <div className="font-semibold text-dark group-hover:text-accent-primary transition-colors">{asset.name}</div>
                        <div className="text-sm text-subtle">{asset.format} • {asset.description}</div>
                      </div>
                    </div>
                    <Download className="w-5 h-5 text-accent-primary opacity-50 group-hover:opacity-100 transition-opacity" />
                  </a>
                ))}
              </div>
            )}
            
            {mediaKit?.brand_colors && (
              <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="font-bold text-dark mb-4">Brand Colors</h3>
                <div className="flex gap-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg mr-2" style={{ backgroundColor: mediaKit.brand_colors.primary_green }}></div>
                    <span className="text-sm text-subtle">Primary Green {mediaKit.brand_colors.primary_green}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg mr-2" style={{ backgroundColor: mediaKit.brand_colors.dark }}></div>
                    <span className="text-sm text-subtle">Dark {mediaKit.brand_colors.dark}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg mr-2" style={{ backgroundColor: mediaKit.brand_colors.gold_accent }}></div>
                    <span className="text-sm text-subtle">Gold Accent {mediaKit.brand_colors.gold_accent}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Press Releases */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-dark">Press Releases</h2>
              <FileText className="w-8 h-8 text-accent-primary" />
            </div>
            <div className="space-y-6">
              {pressReleases.map((release, index) => (
                <div key={index} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className="inline-block bg-accent-primary/10 text-accent-primary text-sm font-semibold px-3 py-1 rounded-full mb-2">
                        {release.date}
                      </span>
                      <h3 className="text-xl font-bold text-dark mb-2">{release.title}</h3>
                      <p className="text-subtle">{release.summary}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Press Release */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 mb-12 border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-dark">Submit a Press Release</h2>
                <p className="text-subtle mt-2">Are you a journalist or PR professional? Submit news about Need A Hair Makeover.</p>
              </div>
              <button
                onClick={() => setShowPressForm(!showPressForm)}
                className="bg-accent-primary hover:bg-accent-primary/90 px-6 py-3 rounded-full font-semibold transition-colors"
                style={{ color: 'white' }}
              >
                {showPressForm ? 'Hide Form' : 'Submit Release'}
              </button>
            </div>
            
            {showPressForm && (
              <div className="bg-white rounded-xl p-6 mt-6">
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-accent-primary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-dark mb-2">Press Release Submitted!</h3>
                    <p className="text-subtle">Our team will review your submission within 48 hours.</p>
                    <button
                      onClick={() => { setSubmitted(false); setShowPressForm(false); }}
                      className="mt-6 text-accent-primary font-semibold hover:underline"
                    >
                      Submit Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitPressRelease} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-dark mb-2">Your Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.author_name}
                          onChange={(e) => setFormData({...formData, author_name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                          placeholder="Jane Smith"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-dark mb-2">Email *</label>
                        <input
                          type="email"
                          required
                          value={formData.author_email}
                          onChange={(e) => setFormData({...formData, author_email: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                          placeholder="jane@publication.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-dark mb-2">Company/Publication</label>
                        <input
                          type="text"
                          value={formData.author_company}
                          onChange={(e) => setFormData({...formData, author_company: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                          placeholder="Beauty Weekly Magazine"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-dark mb-2">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({...formData, category: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                        >
                          <option value="General">General News</option>
                          <option value="Awards">Awards & Recognition</option>
                          <option value="Expansion">Business Expansion</option>
                          <option value="Product">Product/Service Launch</option>
                          <option value="Partnership">Partnership Announcement</option>
                          <option value="Event">Event Coverage</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-dark mb-2">Press Release Title *</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                        placeholder="Need A Hair Makeover Announces..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-dark mb-2">Summary (1-2 sentences) *</label>
                      <textarea
                        required
                        rows={2}
                        value={formData.summary}
                        onChange={(e) => setFormData({...formData, summary: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                        placeholder="Brief summary of the announcement..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-dark mb-2">Full Content *</label>
                      <textarea
                        required
                        rows={8}
                        value={formData.content}
                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                        placeholder="Full press release content..."
                      />
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center bg-accent-primary hover:bg-accent-primary/90 px-8 py-3 rounded-full font-semibold transition-colors disabled:opacity-50"
                        style={{ color: 'white' }}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Submit Press Release
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Awards & Recognition */}
          <div className="bg-gradient-to-r from-accent-primary to-accent-primary/80 text-white rounded-2xl shadow-xl p-8 mb-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold">Awards & Recognition</h2>
              <Award className="w-8 h-8" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {awards.map((yearGroup, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-4xl font-bold mb-4 text-white/90">{yearGroup.year}</div>
                  <ul className="space-y-2">
                    {yearGroup.awards.map((award, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-2">🏆</span>
                        <span className="text-sm">{award}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Interview Topics */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-6">Interview Topics</h2>
            <p className="text-subtle mb-6">
              Our founder and team are available for interviews and expert commentary on:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Hair loss solutions and confidence restoration',
                'Women in business and entrepreneurship',
                'Franchise opportunities in the beauty industry',
                'Hair extension techniques and innovations',
                'Building a specialist salon business',
                'Training and professional development in beauty',
                'UK and international expansion strategies',
                'The future of the hair and beauty industry'
              ].map((topic, index) => (
                <div key={index} className="flex items-center p-3 bg-white rounded-lg">
                  <span className="w-2 h-2 bg-accent-primary rounded-full mr-3"></span>
                  <span className="text-dark">{topic}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="bg-dark text-white rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Get in Touch</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              For media enquiries, interview requests, or partnership opportunities, our team is ready to assist.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:needahairmakeover@gmail.com"
                className="inline-flex items-center justify-center bg-accent-primary hover:bg-accent-primary/90 px-8 py-3 rounded-full font-semibold transition-colors"
                style={{ color: 'white' }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Us
              </a>
              <a 
                href="tel:+442030514338"
                className="inline-flex items-center justify-center bg-white text-dark hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-colors"
              >
                +44 20 3051 4338
              </a>
            </div>
          </div>

        </div>
      </div>
      
      <Footer />
    </div>
  );
};
